import { useIntl } from 'react-intl';
import { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { Dropdown } from 'react-bootstrap';
import Pagination from '@mui/material/Pagination';
import {
  generateEdiFile,
  generatePdfFile,
  getInvoiceById,
  BulkActionsEnum,
} from '../services/InvoiceCRUD';
import { InvoiceModel, InvoiceStateEnum } from '../models/InvoiceModel';
import { INVOICES_PER_PAGE } from '../services/InvoiceCRUD';
import { useSelector } from 'react-redux';
import accountingGroupEmails from '../constants/accountingGroupEmails';
import ediUploadUsers from '../constants/ediUploadUsers';
import { authUserSelector } from '../../auth';

const SUCCESS_STATES = [InvoiceStateEnum.PAID];
const FAIL_STATES = [InvoiceStateEnum.CANCELLED];

const STATE_TRANSITIONS = {
  [InvoiceStateEnum.PENDING_PAYMENT]: [
    InvoiceStateEnum.PAID,
    InvoiceStateEnum.CANCELLED,
    InvoiceStateEnum.PARTIAL,
  ],
  [InvoiceStateEnum.PAID]: [
    InvoiceStateEnum.PARTIAL,
    InvoiceStateEnum.REFUNDED,
  ],
  [InvoiceStateEnum.CANCELLED]: [InvoiceStateEnum.PENDING_PAYMENT],
  [InvoiceStateEnum.PARTIAL]: [
    InvoiceStateEnum.PAID,
    InvoiceStateEnum.REFUNDED,
  ],
};

type PropsType = {
  invoices: Array<InvoiceModel>;
  loading: boolean;
  children: ReactNode;
  page?: number;
  count?: number;
  onUpdateState: (invoice: InvoiceModel, state: InvoiceStateEnum) => void;
  onPageChange?: (page: number) => void;
  onCreateCreditNote: (invoice: InvoiceModel) => void;
  onUpdateAccounted: (invoice: InvoiceModel, accounted: boolean) => void;
  onUploadEdi: (invoice: InvoiceModel) => Promise<void>;
  onPerformBulkAction?: (
    action: BulkActionsEnum,
    invoiceIds: Array<number>,
    options?: { responseType?: 'blob' | 'json' }
  ) => Promise<any>;
};

export default function InvoiceList({
  invoices,
  loading,
  page,
  count,
  children,
  onUpdateState,
  onPageChange,
  onCreateCreditNote,
  onUpdateAccounted,
  onUploadEdi,
  onPerformBulkAction,
}: PropsType) {
  const intl = useIntl();
  const urlParams = new URLSearchParams(window.location.search);
  const invoiceId = urlParams.get('invoice');
  const [generatingEdi, setGeneratingEdi] = useState<InvoiceModel | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<InvoiceModel | null>(null);
  const [uploadingEdi, setUploadingEdi] = useState<InvoiceModel | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceModel[]>([]);
  const hasSelectedAllInvoices = selectedInvoices?.length === invoices?.length;
  const selectedCount = hasSelectedAllInvoices
    ? count
    : selectedInvoices.length;
  const [bulkState, setBulkState] = useState<'loading' | 'idle'>('idle');
  const authUser = useSelector(authUserSelector);

  useEffect(() => {
    setSelectedInvoices([]);
  }, [invoices]);

  async function exportInvoices() {
    try {
      const hasConfirmed = window.confirm(
        intl.formatMessage(
          { id: 'invoices.bulkExportInvoicesConfirmation' },
          { count: selectedCount }
        )
      );
      if (!hasConfirmed) return;
      setBulkState('loading');
      let response;
      if (hasSelectedAllInvoices) {
        response = await onPerformBulkAction(
          BulkActionsEnum.EXPORT_INVOICES,
          [],
          { responseType: 'blob' }
        );
      } else {
        response = await onPerformBulkAction(
          BulkActionsEnum.EXPORT_INVOICES,
          selectedInvoices?.map((inv) => inv.id),
          { responseType: 'blob' }
        );
      }
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.ms-excel',
      });
      saveAs(blob, `facturas-b2b-${new Date().toISOString().slice(0, 10)}.xls`);
      toast.success(
        intl.formatMessage(
          { id: 'invoices.bulkExportInvoicesSuccess' },
          { count: selectedCount }
        )
      );
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'invoices.bulkExportInvoicesError' })
      );
      console.warn(error);
    } finally {
      setBulkState('idle');
    }
  }

  function getInvoices() {
    if (invoiceId) {
      const invoice = invoices.find(
        (invoice) => invoice.id === parseInt(invoiceId)
      );
      if (invoice) {
        return [
          invoice,
          ...invoices
            .filter((i) => i.id !== invoice.id)
            .sort((a, b) => b.number.localeCompare(a.number)),
        ];
      }
    }
    return invoices.sort((a, b) => b.number.localeCompare(a.number));
  }

  function downloadFile(url, fileName) {
    const element = document.createElement('a');
    element.target = '_blank';
    element.href = url;
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
  }

  async function onGenerateEdi(invoice: InvoiceModel) {
    try {
      setGeneratingEdi(invoice);
      if (invoice.edi_file_url) {
        downloadFile(invoice.edi_file_url, `${invoice.code}.txt`);
      } else {
        await generateEdiFile(invoice.id);
        const { data } = await getInvoiceById(invoice.id);
        if (data.edi_file_url) {
          downloadFile(data.edi_file_url, `${data.code}.txt`);
        } else {
          toast.error(
            intl.formatMessage({ id: 'invoices.ediGenerationError' })
          );
        }
      }
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'invoices.ediGenerationError' }));
      console.warn(error);
    } finally {
      setGeneratingEdi(null);
    }
  }

  async function onUploadEdiClick(invoice: InvoiceModel) {
    setUploadingEdi(invoice);
    await onUploadEdi(invoice);
    setUploadingEdi(null);
  }

  async function onGeneratePdf(invoice: InvoiceModel) {
    try {
      setGeneratingPdf(invoice);
      if (invoice.pdf_file_url) {
        downloadFile(invoice.pdf_file_url, `${invoice.code}.pdf`);
      } else {
        await generatePdfFile(invoice.id);
        const { data } = await getInvoiceById(invoice.id);
        if (data.pdf_file_url) {
          downloadFile(data.pdf_file_url, `${data.code}.pdf`);
        } else {
          toast.error(
            intl.formatMessage({ id: 'invoices.pdfGenerationError' })
          );
        }
      }
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'invoices.pdfGenerationError' }));
      console.warn(error);
    } finally {
      setGeneratingPdf(null);
    }
  }

  async function toggleAccounted(invoice: InvoiceModel) {
    onUpdateAccounted(invoice, !invoice.accounted);
  }

  return (
    <>
      <div className="card">
        <div className="card-body">
          <h2 className="mb-8">
            {intl.formatMessage({ id: 'invoices.invoiceList' })}
          </h2>
          <div className="row mb-8">{children}</div>
          {loading && <div className="spinner-border text-primary my-10" />}
          {!loading && invoices?.length === 0 && (
            <p>{intl.formatMessage({ id: 'invoices.noInvoices' })}</p>
          )}
          {!loading && onPerformBulkAction && (
            <section className="d-flex px-0 gap-4 align-items-end mb-8">
              <div className="me-4">
                <label className={clsx('mb-2', !selectedCount && 'text-muted')}>
                  {selectedCount > 0
                    ? intl.formatMessage(
                        { id: 'invoices.countSelectedInvoices' },
                        { count: selectedCount }
                      )
                    : intl.formatMessage({ id: 'invoices.noSelectedInvoices' })}
                </label>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="primary"
                    disabled={selectedCount < 1 || bulkState === 'loading'}
                  >
                    <span className="me-4 fas w-15px d-inline-block fa-layer-group" />
                    {intl.formatMessage({ id: 'invoices.bulkActions' })}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      className="py-3 ps-5 fs-6"
                      onClick={exportInvoices}
                    >
                      <span className="me-4 fas w-15px d-inline-block fa-file-excel text-success" />
                      {intl.formatMessage({ id: 'invoices.exportInvoices' })}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </section>
          )}
          {!loading && invoices?.length > 0 && (
            <div
              className="table-responsive"
              style={{
                height: 'calc(100vh - 420px)',
              }}
            >
              <p className="text-muted">
                {intl.formatMessage(
                  { id: 'invoices.count' },
                  {
                    count,
                    visible: invoices.length,
                  }
                )}
              </p>
              <table className="table table-hover table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                <thead className="table-header sticky-top bg-white">
                  <tr className="text-left fw-bolder">
                    {onPerformBulkAction && (
                      <th className="text-center ps-6 pe-4">
                        <input
                          type="checkbox"
                          className="cursor-pointer form-check w-25px h-25px"
                          disabled={bulkState === 'loading'}
                          checked={
                            selectedInvoices.length === invoices.length &&
                            invoices.length > 0
                          }
                          onChange={() => {
                            if (selectedInvoices.length === invoices.length) {
                              setSelectedInvoices([]);
                            } else {
                              setSelectedInvoices(invoices);
                            }
                          }}
                        />
                      </th>
                    )}
                    <th className="ps-4">
                      {intl.formatMessage({ id: 'invoices.code' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.orderCodeColumn' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.storeName' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.accounted' })}
                    </th>
                    <th>{intl.formatMessage({ id: 'invoices.number' })}</th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.order' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.date' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.dueDate' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.amount' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.kind' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.state' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.edi' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.pdf' })}
                    </th>
                    <th className="">
                      {intl.formatMessage({ id: 'invoices.createCreditNote' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getInvoices()?.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className={clsx({
                        'align-middle ': true,
                        'bg-light-success':
                          invoiceId && invoiceId === invoice.id.toString(),
                      })}
                    >
                      {onPerformBulkAction && (
                        <td className="text-center ps-6 pe-4">
                          <input
                            type="checkbox"
                            className="cursor-pointer form-check w-25px h-25px"
                            disabled={bulkState === 'loading'}
                            checked={selectedInvoices.some(
                              (i) => i.id === invoice.id
                            )}
                            onChange={() => {
                              if (
                                selectedInvoices.some(
                                  (i) => i.id === invoice.id
                                )
                              ) {
                                setSelectedInvoices(
                                  selectedInvoices.filter(
                                    (i) => i.id !== invoice.id
                                  )
                                );
                              } else {
                                setSelectedInvoices([
                                  ...selectedInvoices,
                                  invoice,
                                ]);
                              }
                            }}
                          />
                        </td>
                      )}
                      <td className="ps-4">{invoice.code}</td>
                      <td className="">{invoice.order_code}</td>
                      <td className="">{invoice.store_name ?? '-'}</td>
                      <td className="">
                        <div className="form-switch d-flex gap-2">
                          <input
                            disabled={
                              !accountingGroupEmails.includes(authUser?.email)
                            }
                            type="checkbox"
                            className="form-check-input mt-0"
                            onChange={() => toggleAccounted(invoice)}
                            checked={invoice.accounted}
                          />
                          <label className="form-check-label">
                            {invoice.accounted
                              ? intl.formatMessage({
                                  id: 'invoices.accountedYes',
                                })
                              : intl.formatMessage({
                                  id: 'invoices.accountedNo',
                                })}
                          </label>
                        </div>
                      </td>
                      <td className="">{invoice.number}</td>
                      <td className="">{invoice.order}</td>
                      <td className="">
                        {invoice.date
                          ? new Date(invoice.date).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="">
                        {invoice.due_date
                          ? new Date(invoice.due_date).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="">
                        {invoice.total_with_taxes != null
                          ? intl.formatNumber(invoice.total_with_taxes, {
                              style: 'currency',
                              currency: 'EUR',
                            })
                          : '-'}
                      </td>
                      <td className="">
                        {invoice.is_proforma && (
                          <span className="badge badge-light">
                            {intl.formatMessage({ id: 'invoices.proforma' })}
                          </span>
                        )}
                        {invoice.is_credit && (
                          <span className="badge badge-warning text-dark">
                            {intl.formatMessage({ id: 'invoices.credit' })}
                          </span>
                        )}
                        {invoice.is_preview && (
                          <span className="badge badge-light-primary">
                            {intl.formatMessage({ id: 'invoices.preview' })}
                          </span>
                        )}
                        {!invoice.is_proforma &&
                          !invoice.is_credit &&
                          !invoice.is_preview && (
                            <span className="badge badge-primary">
                              {intl.formatMessage({ id: 'invoices.invoice' })}
                            </span>
                          )}
                      </td>
                      <td className="">
                        {!invoice.is_proforma &&
                          STATE_TRANSITIONS[invoice.state]?.length > 0 && (
                            <Dropdown>
                              <Dropdown.Toggle
                                id="dropdown-create-b2b-invoice"
                                variant={clsx({
                                  primary:
                                    !SUCCESS_STATES.includes(invoice.state) &&
                                    !FAIL_STATES.includes(invoice.state),
                                  success: SUCCESS_STATES.includes(
                                    invoice.state
                                  ),
                                  danger: FAIL_STATES.includes(invoice.state),
                                })}
                                className={clsx({
                                  'badge badge-sm px-4 py-2': true,
                                })}
                              >
                                {invoice.state}
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                {STATE_TRANSITIONS[invoice.state]?.map(
                                  (state) => (
                                    <Dropdown.Item
                                      className="p-4"
                                      key={state}
                                      onClick={() =>
                                        onUpdateState(invoice, state)
                                      }
                                    >
                                      {state}
                                    </Dropdown.Item>
                                  )
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        {!invoice.is_proforma &&
                          !STATE_TRANSITIONS[invoice.state]?.length && (
                            <span
                              className={clsx({
                                'badge badge-sm px-4 py-2': true,
                                'badge-success': SUCCESS_STATES.includes(
                                  invoice.state
                                ),
                                'badge-danger': FAIL_STATES.includes(
                                  invoice.state
                                ),
                              })}
                            >
                              {invoice.state}
                            </span>
                          )}
                        {invoice.is_proforma && '-'}
                      </td>
                      <td className="">
                        {invoice.distributor_supports_edi ? (
                          <>
                            <button
                              onClick={() => onGenerateEdi(invoice)}
                              disabled={!!generatingEdi}
                              className={clsx({
                                'btn btn-sm btn-tertiary text-primary': true,
                                'fa fa-download':
                                  generatingEdi?.id !== invoice.id,
                              })}
                            >
                              {generatingEdi?.id === invoice.id && (
                                <span className="spinner-border text-primary spinner-border-sm" />
                              )}
                            </button>
                            {ediUploadUsers.includes(authUser?.email) && (
                              <button
                                onClick={() => onUploadEdiClick(invoice)}
                                disabled={!!uploadingEdi}
                                title={
                                  invoice.edi_uploaded_at
                                    ? intl.formatMessage(
                                        { id: 'edi.uploadedAt' },
                                        {
                                          date: new Date(
                                            invoice.edi_uploaded_at
                                          ).toLocaleString(),
                                        }
                                      )
                                    : intl.formatMessage({ id: 'edi.upload' })
                                }
                                className={clsx({
                                  'btn btn-sm btn-tertiary': true,
                                  'text-success': !!invoice.edi_uploaded_at,
                                  'text-primary': !invoice.edi_uploaded_at,
                                  'fa fa-upload':
                                    uploadingEdi?.id !== invoice.id,
                                })}
                              >
                                {uploadingEdi?.id === invoice.id && (
                                  <span className="spinner-border text-primary spinner-border-sm" />
                                )}
                              </button>
                            )}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="">
                        <button
                          onClick={() => onGeneratePdf(invoice)}
                          disabled={!!generatingPdf}
                          className={clsx({
                            'btn btn-sm btn-tertiary text-primary': true,
                            'fa fa-download': generatingPdf?.id !== invoice.id,
                          })}
                        >
                          {generatingPdf?.id === invoice.id && (
                            <span className="spinner-border text-primary spinner-border-sm" />
                          )}
                        </button>
                      </td>
                      <td className="">
                        {!invoice.is_credit && !invoice.is_proforma && (
                          <button
                            className="btn btn-sm btn-tertiary text-primary fa fa-file-invoice"
                            onClick={() => onCreateCreditNote(invoice)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {count > 0 && (
                <Pagination
                  className="d-flex justify-content-center"
                  count={Math.ceil(count / INVOICES_PER_PAGE)}
                  page={page}
                  onChange={(e, page) => onPageChange && onPageChange(page)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
